import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BackButton } from '@/components/shared/BackButton';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchEmailSettings, updateEmailSettings, type EmailSettings } from '../../lib/inboxSettingsApi';
import { QueryErrorState } from '@/components/shared/QueryErrorState';

type PartialSettings = Partial<Omit<EmailSettings, 'userId' | 'updatedAt'>>;

export default function InboxGeneralSettingsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: settings, isLoading, isError, refetch } = useQuery({
    queryKey: ['inbox-settings'],
    queryFn: fetchEmailSettings,
  });

  const [form, setForm] = useState<PartialSettings>({});
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (settings) {
      setForm({
        readingPanePosition: settings.readingPanePosition,
        density: settings.density,
        conversationMode: settings.conversationMode,
        autoMarkReadOnOpen: settings.autoMarkReadOnOpen,
        undoSendWindowSeconds: settings.undoSendWindowSeconds,
        defaultReplyBehaviour: settings.defaultReplyBehaviour,
        showExternalImages: settings.showExternalImages,
        quietHoursEnabled: settings.quietHoursEnabled,
        quietHoursStart: settings.quietHoursStart ?? '09:00',
        quietHoursEnd: settings.quietHoursEnd ?? '18:00',
        digestMode: settings.digestMode,
        vacationResponderEnabled: settings.vacationResponderEnabled,
        vacationResponderMessage: settings.vacationResponderMessage ?? '',
        vacationResponderFrom: settings.vacationResponderFrom ?? '',
        vacationResponderTo: settings.vacationResponderTo ?? '',
      });
    }
  }, [settings]);

  const saveMutation = useMutation({
    mutationFn: () => updateEmailSettings(form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inbox-settings'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    },
  });

  function patch(updates: PartialSettings) {
    setForm((prev) => ({ ...prev, ...updates }));
  }

  if (isLoading) {
    return (
      <div style={{
        maxWidth: 640, margin: '0 auto', padding: '80px 24px',
        fontFamily: "'IBM Plex Sans', sans-serif", textAlign: 'center', color: 'var(--text-muted)',
      }}>
        Loading settings…
      </div>
    );
  }

  // Without this the form fell through to its own defaults: every control showed a value nobody
  // had chosen, and the first Save wrote that whole invented set over the real settings.
  if (isError) {
    return (
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '40px 24px' }}>
        <QueryErrorState what="your email settings" onRetry={() => void refetch()} />
      </div>
    );
  }

  const selectStyle: React.CSSProperties = {
    border: '1px solid var(--border-strong)', borderRadius: 6,
    padding: '10px 12px', fontSize: 16, color: 'var(--text-primary)',
    fontFamily: "'IBM Plex Sans', sans-serif",
    outline: 'none', cursor: 'pointer',
    // `backgroundColor`, not the `background` shorthand: the shorthand resets background-image
    // to none, which wipes the calendar/clock icon globals.css paints on date and time inputs.
    backgroundColor: 'var(--surface-card)',
    // Native date/time inputs carry a wide intrinsic minimum on iOS. Without this they refuse
    // to shrink inside their column and push straight out through the side of the card.
    minWidth: 0, maxWidth: '100%',
  };

  const inputStyle: React.CSSProperties = {
    ...selectStyle, cursor: 'text',
  };

  /**
   * Date and time inputs only — NOT the selects.
   *
   * iOS Safari keeps its own native sizing for `type="date"` and `type="time"` and ignores
   * `width: 100%`, so these four fields rendered wider than the card and crossed its right
   * border while the textarea beside them sat inside it perfectly. Clearing `-webkit-appearance`
   * makes them ordinary boxes that honour the width they are given.
   *
   * This cannot go on `selectStyle`: `appearance: none` on a <select> also removes the
   * dropdown arrow, leaving a box with no affordance.
   */
  /**
   * The iOS sizing fix, the icon and the collapse floor all now live in `globals.css` under
   * `input[type="date"], input[type="time"]`, so they apply to every date field in the app
   * rather than only this page. What stays here is page-specific: full width inside its column,
   * and the 44px touch target these settings controls use.
   */
  const dateTimeStyle: React.CSSProperties = {
    ...inputStyle,
    width: '100%', boxSizing: 'border-box',
    minHeight: 44, lineHeight: '24px',
  };



  const rowStyle: React.CSSProperties = {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    flexWrap: 'wrap', gap: 12,
    padding: '14px 0', borderBottom: '1px solid var(--border-subtle)',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 14, color: 'var(--text-primary)',
  };

  const subLabelStyle: React.CSSProperties = {
    fontSize: 12, color: 'var(--text-muted)', marginTop: 2,
  };

  return (
    <div style={{
      maxWidth: 640, margin: '0 auto',
      padding: 'clamp(20px, 5vw, 40px) clamp(14px, 4vw, 24px)',
      fontFamily: "'IBM Plex Sans', sans-serif",
    }}>
      {/* Was an unlabelled arrow wired to a fixed destination. These pages are opened from the
          inbox, from the Email settings hub and from Settings, so a fixed target sent people
          somewhere they had never been. Back means back — same control as every other page. */}
      <BackButton onClick={() => navigate(-1)} />

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16, marginBottom: 32 }}>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
            General settings
          </h1>
        </div>
        <button
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending}
          style={{
            padding: '8px 20px', borderRadius: 6,
            background: saved ? '#166534' : '#1e3a5f',
            color: 'white', border: 'none',
            cursor: 'pointer', fontSize: 13, fontWeight: 500,
            transition: 'background 0.2s',
            opacity: saveMutation.isPending ? 0.6 : 1,
          }}
        >
          {saveMutation.isPending ? 'Saving…' : saved ? 'Saved' : 'Save changes'}
        </button>
      </div>

      {/* Reading experience */}
      <Section title="Reading experience">
        <div style={rowStyle}>
          <div>
            <div style={labelStyle}>Reading pane<NotYetApplied /></div>
            <div style={subLabelStyle}>Where the email content appears relative to the list</div>
          </div>
          <select
            value={form.readingPanePosition}
            onChange={(e) => patch({ readingPanePosition: e.target.value as EmailSettings['readingPanePosition'] })}
            style={selectStyle}
          >
            <option value="right">Right</option>
            <option value="bottom">Bottom</option>
            <option value="off">Off</option>
          </select>
        </div>
        {/*
          DENSITY IS NOT SET HERE ANY MORE.

          There were two: `EmailSettings.density` written by this page, and
          `UserPreference.density` written by Settings → Appearance. Same two values, same
          meaning, two stores — and neither of them was read by anything, so both screens
          offered a control that changed nothing and could disagree with each other the moment
          one started working. Density is one preference for the whole application, so it now
          lives with the rest of a person's appearance settings and the inbox list obeys it.
        */}
        <div style={rowStyle}>
          <div>
            <div style={labelStyle}>Density</div>
            <div style={subLabelStyle}>Set once for the whole platform, in Appearance settings</div>
          </div>
          <Link to="/barrister/settings/section/appearance" style={{ ...selectStyle, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}>
            Open Appearance
          </Link>
        </div>
        <div style={rowStyle}>
          <div>
            <div style={labelStyle}>Conversation mode</div>
            <div style={subLabelStyle}>Group related emails into threads</div>
          </div>
          <Toggle
            checked={form.conversationMode ?? true}
            onChange={(v) => patch({ conversationMode: v })}
          />
        </div>
        <div style={rowStyle}>
          <div>
            <div style={labelStyle}>Auto mark as read</div>
            <div style={subLabelStyle}>Mark emails as read when you open them</div>
          </div>
          <Toggle
            checked={form.autoMarkReadOnOpen ?? true}
            onChange={(v) => patch({ autoMarkReadOnOpen: v })}
          />
        </div>
        <div style={rowStyle}>
          <div>
            <div style={labelStyle}>Show external images</div>
            <div style={subLabelStyle}>Control when remote images are loaded</div>
          </div>
          <select
            value={form.showExternalImages}
            onChange={(e) => patch({ showExternalImages: e.target.value as EmailSettings['showExternalImages'] })}
            style={selectStyle}
          >
            <option value="ask">Ask each time</option>
            <option value="always">Always load</option>
            <option value="block">Never load</option>
          </select>
        </div>
      </Section>

      {/* Sending */}
      <Section title="Sending">
        <div style={rowStyle}>
          <div>
            <div style={labelStyle}>Undo send window<NotYetApplied /></div>
            <div style={subLabelStyle}>Seconds to delay sending so you can cancel</div>
          </div>
          <select
            value={form.undoSendWindowSeconds}
            onChange={(e) => patch({ undoSendWindowSeconds: parseInt(e.target.value) })}
            style={selectStyle}
          >
            <option value={0}>Disabled</option>
            <option value={5}>5 seconds</option>
            <option value={10}>10 seconds</option>
            <option value={30}>30 seconds</option>
          </select>
        </div>
        <div style={rowStyle}>
          <div>
            <div style={labelStyle}>Default reply behaviour</div>
            <div style={subLabelStyle}>When you click Reply, who is included by default</div>
          </div>
          <select
            value={form.defaultReplyBehaviour}
            onChange={(e) => patch({ defaultReplyBehaviour: e.target.value as EmailSettings['defaultReplyBehaviour'] })}
            style={selectStyle}
          >
            <option value="reply">Reply to sender only</option>
            <option value="reply_all">Reply to all</option>
          </select>
        </div>
      </Section>

      {/* Notifications */}
      <Section title="Notifications">
        <div style={rowStyle}>
          <div>
            <div style={labelStyle}>Delivery mode</div>
            <div style={subLabelStyle}>How you receive email digests</div>
          </div>
          <select
            value={form.digestMode}
            onChange={(e) => patch({ digestMode: e.target.value as EmailSettings['digestMode'] })}
            style={selectStyle}
          >
            <option value="immediate">Immediate</option>
            <option value="hourly">Hourly digest</option>
            <option value="daily">Daily digest</option>
          </select>
        </div>
        <div style={rowStyle}>
          <div>
            <div style={labelStyle}>Quiet hours<NotYetApplied /></div>
            <div style={subLabelStyle}>Suppress notifications during these hours</div>
          </div>
          <Toggle
            checked={form.quietHoursEnabled ?? false}
            onChange={(v) => patch({ quietHoursEnabled: v })}
          />
        </div>
        {form.quietHoursEnabled && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, paddingBottom: 12 }}>
            <div style={{ flex: '1 1 220px', minWidth: 0 }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>From</div>
              <input
                type="time"
                value={form.quietHoursStart ?? '09:00'}
                onChange={(e) => patch({ quietHoursStart: e.target.value })}
                style={dateTimeStyle}
              />
            </div>
            <div style={{ flex: '1 1 220px', minWidth: 0 }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>To</div>
              <input
                type="time"
                value={form.quietHoursEnd ?? '18:00'}
                onChange={(e) => patch({ quietHoursEnd: e.target.value })}
                style={dateTimeStyle}
              />
            </div>
          </div>
        )}
      </Section>

      {/* Vacation responder */}
      <Section title="Vacation responder">
        <div style={rowStyle}>
          <div>
            <div style={labelStyle}>Vacation responder<NotYetApplied /></div>
            <div style={subLabelStyle}>Automatically reply to incoming emails when away</div>
          </div>
          <Toggle
            checked={form.vacationResponderEnabled ?? false}
            onChange={(v) => patch({ vacationResponderEnabled: v })}
          />
        </div>
        {form.vacationResponderEnabled && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingBottom: 14 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>From date</div>
                <input
                  type="date"
                  value={form.vacationResponderFrom ?? ''}
                  onChange={(e) => patch({ vacationResponderFrom: e.target.value })}
                  style={dateTimeStyle}
                />
              </div>
              <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>To date</div>
                <input
                  type="date"
                  value={form.vacationResponderTo ?? ''}
                  onChange={(e) => patch({ vacationResponderTo: e.target.value })}
                  style={dateTimeStyle}
                />
              </div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>Auto-reply message</div>
              <textarea
                value={form.vacationResponderMessage ?? ''}
                onChange={(e) => patch({ vacationResponderMessage: e.target.value })}
                style={{
                  ...inputStyle,
                  width: '100%', boxSizing: 'border-box' as const,
                  minHeight: 100, resize: 'vertical', lineHeight: 1.5,
                }}
                placeholder="I am currently out of chambers and will return on [date]. For urgent matters, please contact my clerk."
              />
            </div>
          </div>
        )}
      </Section>
    </div>
  );
}

/**
 * "Saved, not yet applied".
 *
 * These four preferences persist correctly and are returned by the API, but nothing consumes them
 * at runtime yet: no code lays the inbox out from the reading-pane position, delays a send by the
 * undo-send window, consults quiet hours, or sends a vacation reply. The Settings shell already
 * tells the truth about them (the registry marks them PARTIAL); the pages where the controls
 * actually live did not, so a barrister could set one here and reasonably conclude it had taken
 * effect. Saying so is the smallest honest fix — 4C-5 does not build the missing consumers.
 */
function NotYetApplied() {
  return (
    <span
      title="This preference is saved to your account, but nothing acts on it yet."
      style={{
        marginLeft: 8, padding: '1px 6px', borderRadius: 4,
        fontSize: 10, fontWeight: 600, letterSpacing: '0.02em',
        textTransform: 'uppercase', whiteSpace: 'nowrap',
        color: 'var(--amber-700, #b45309)',
        border: '1px solid var(--amber-400, rgba(180,83,9,0.35))',
      }}
    >
      Saved, not yet applied
    </span>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{
        fontSize: 11, fontWeight: 600, color: 'var(--text-muted)',
        textTransform: 'uppercase', letterSpacing: '0.06em',
        marginBottom: 6,
      }}>
        {title}
      </div>
      <div style={{ border: '1px solid var(--border-default)', borderRadius: 8, background: 'var(--surface-card)', padding: '0 16px' }}>
        {children}
      </div>
    </div>
  );
}

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      onClick={() => onChange(!checked)}
      style={{
        width: 44, height: 24, borderRadius: 12,
        background: checked ? '#1e3a5f' : '#d1d5db',
        border: 'none', cursor: 'pointer', position: 'relative',
        transition: 'background 0.15s', flexShrink: 0,
      }}
      aria-checked={checked}
      role="switch"
    >
      <div style={{
        width: 18, height: 18, borderRadius: '50%',
        background: 'var(--surface-card)',
        position: 'absolute',
        top: 3,
        left: checked ? 23 : 3,
        transition: 'left 0.15s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      }} />
    </button>
  );
}
